import { JSXElementConstructor, useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faImageSlash } from '@fortawesome/pro-solid-svg-icons'
import {
  GenericError,
  ErrorBoundary,
  LazySvg,
  LazySvgProps
} from '@commutifi-fe/ui'
import { colors } from '@commutifi-fe/ui/styles/constants'
import cx from 'classnames'
import { useLocaleReceiver } from '@commutifi-fe/ui/components/locales/LocaleReceiver'

const Icon = ({
  image,
  fallback = null,
  ...svgProps
}: CommutifiSvgProps & { fallback: React.ReactNode }) => {
  const ImportedIconRef = useRef<JSXElementConstructor<
    Omit<CommutifiSvgProps, 'image'>
  > | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
  }, [image])

  useEffect(() => {
    const importIcon = async () => {
      try {
        const Icon = (await import(`../${image}`)).default
        ImportedIconRef.current = Icon
      } catch (err) {
        throw err
      } finally {
        setLoading(false)
      }
    }

    if (loading) {
      importIcon()
    }
  }, [image, loading])

  if (ImportedIconRef.current) {
    const { current: ImportedIcon } = ImportedIconRef
    return <ImportedIcon {...svgProps} />
  }

  return fallback
}

export interface CommutifiSvgProps extends Omit<LazySvgProps, 'src'> {
  image: ImageName
}

enum ImageName {
  Amenities = 'Amenities',
  Analytics = 'Analytics',
  Bike = 'Bike',
  BikeParkingGarage = 'BikeParkingGarage',
  Bikeshare = 'Bikeshare',
  Car = 'Car',
  CardBenefits = 'CardBenefits',
  Carpool = 'Carpool',
  Carshare = 'Carshare',
  Closed = 'Closed',
  Coins = 'Coins',
  CommuterBenefits = 'CommuterBenefits',
  Compass = 'Compass',
  CustomCommute = 'CustomCommute',
  ElectricCharging = 'ElectricCharging',
  EmptyBookings = 'EmptyBookings',
  EmptyAnalytics = 'Analytics',
  EmptyEmployees = 'EmptyEmployees',
  EmptyInvoices = 'Invoice',
  EmptyLocation = 'EmptyLocations',
  EmptyPlans = 'EmptyPlans',
  EmptyWallets = 'EmptyWallets',
  EmptyPasses = 'EmptyPasses',
  EmptyVehicles = 'EmptyVehicles',
  EmptySurvey = 'EmptySurvey',
  Errands = 'Errands',
  Food = 'Food',
  GuaranteedRideHome = 'GuaranteedRideHome',
  Gift = 'Gift',
  ImproveScore = 'ImproveScore',
  Incentives = 'Incentives',
  Invoice = 'Invoice',
  Kids = 'Kids',
  Meeting = 'Meeting',
  OnsiteParking = 'OnsiteParking',
  ParkingTickets = 'ParkingTickets',
  PhysicalActivity = 'PhysicalActivity',
  PublicTransit = 'PublicTransit',
  RemoteWork = 'RemoteWork',
  Seats = 'Seats',
  Shuttle = 'Shuttle',
  Swirl = 'Swirl',
  Vanpool = 'Vanpool',
  Walk = 'Walk',
  Workspace = 'Workspace'
}

export function CommutifiSvg({
  image,
  className,
  ...svgProps
}: CommutifiSvgProps) {
  const intl = useLocaleReceiver()
  return (
    <ErrorBoundary ErrorComponent={GenericError}>
      <LazySvg className={cx('commutifi-svg', className)} {...svgProps}>
        <Icon
          image={image}
          fallback={
            <FontAwesomeIcon
              icon={faImageSlash}
              fontSize={Math.max(svgProps.height - 60, 24)}
              color={colors.neutral400}
            />
          }
          {...svgProps}
        />
      </LazySvg>
    </ErrorBoundary>
  )
}

CommutifiSvg.Images = ImageName

export default CommutifiSvg
